import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirXmark,
  iconoirUpload,
  iconoirUser,
  iconoirLink,
  iconoirCheck,
  iconoirMediaImage
} from '@ng-icons/iconoir';

import { BtnComponent } from '../../buttons/btn/btn.component';
import { AvatarComponent } from '../avatar.component';
import { InputTextComponent } from '../../../forms/input-text/input-text.component';
import { AuthService } from '../../../../core/auth/auth.service';
import { SupabaseService } from '../../../../core/supabase.service';
import { NotificationsService } from '../../../../services/notifications.service';

export interface AvatarChangeDialogData {
  currentPhotoUrl?: string;
  firstName?: string;
  lastName?: string;
}

type AvatarMode = 'current' | 'upload' | 'generated';

@Component({
  selector: 'app-avatar-change-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    BtnComponent,
    InputTextComponent,
    AvatarComponent
  ],
  providers: [
    provideIcons({
      iconoirXmark,
      iconoirUpload,
      iconoirUser,
      iconoirLink,
      iconoirCheck,
      iconoirMediaImage
    })
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2>Changer mon avatar</h2>
        <button class="close-btn" (click)="cancel()">
          <ng-icon name="iconoirXmark" size="20"></ng-icon>
        </button>
      </div>

      <div class="dialog-content">
        <!-- Preview -->
        <div class="avatar-preview-section">
          <div class="avatar-preview" [class.uploading]="isUploading()">
            <!-- Rendu local : photo, ou initiales sur pastille de ton (plus d'appel à ui-avatars.com) -->
            <app-avatar [imagePass]="previewUrl() || ''" [textPass]="fullName()" [sizePass]="100" />
            @if (isUploading()) {
              <div class="upload-overlay">
                <div class="spinner"></div>
              </div>
            }
          </div>
          <p class="preview-label">Aperçu</p>
        </div>

        <!-- Options -->
        <div class="options-section">
          <!-- Upload option -->
          <div class="option-card" [class.active]="selectedMode() === 'upload'" (click)="selectMode('upload')">
            <div class="option-icon">
              <ng-icon name="iconoirUpload" size="24"></ng-icon>
            </div>
            <div class="option-content">
              <h3>Télécharger une image</h3>
              <p>Choisir une photo depuis votre appareil</p>
            </div>
            @if (selectedMode() === 'upload') {
              <ng-icon name="iconoirCheck" size="20" class="check-icon"></ng-icon>
            }
          </div>

          @if (selectedMode() === 'upload') {
            <div class="upload-area"
                 (click)="fileInput.click()"
                 (dragover)="onDragOver($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onDrop($event)"
                 [class.dragover]="isDragOver()">
              <input #fileInput type="file" accept="image/*" (change)="onFileSelected($event)" hidden>
              <ng-icon name="iconoirMediaImage" size="32"></ng-icon>
              <p>Cliquez ou glissez une image ici</p>
              <span class="hint">PNG, JPG jusqu'à 5 Mo</span>
            </div>
          }

          <!-- Generated avatar option -->
          <div class="option-card" [class.active]="selectedMode() === 'generated'" (click)="selectMode('generated')">
            <div class="option-icon">
              <ng-icon name="iconoirUser" size="24"></ng-icon>
            </div>
            <div class="option-content">
              <h3>Avatar généré</h3>
              <p>Utiliser un avatar avec vos initiales</p>
            </div>
            @if (selectedMode() === 'generated') {
              <ng-icon name="iconoirCheck" size="20" class="check-icon"></ng-icon>
            }
          </div>

          <!-- Custom URL option -->
          <div class="option-card" [class.active]="selectedMode() === 'current'" (click)="selectMode('current')">
            <div class="option-icon">
              <ng-icon name="iconoirLink" size="24"></ng-icon>
            </div>
            <div class="option-content">
              <h3>URL personnalisée</h3>
              <p>Entrer l'URL d'une image existante</p>
            </div>
            @if (selectedMode() === 'current') {
              <ng-icon name="iconoirCheck" size="20" class="check-icon"></ng-icon>
            }
          </div>

          @if (selectedMode() === 'current') {
            <div class="url-input-area">
              <app-input-text
                label="URL de l'image"
                placeholder="https://exemple.com/mon-avatar.jpg"
                [(ngModel)]="customUrl"
                (ngModelChange)="onCustomUrlChange($event)">
              </app-input-text>
            </div>
          }
        </div>
      </div>

      <div class="dialog-footer">
        <app-btn [config]="{ type: 'flat', color: 'cream' }" (click)="cancel()">
          Annuler
        </app-btn>
        <app-btn
          [config]="{ type: 'filled', color: 'coffee' }"
          (click)="save()"
          [disabled]="isUploading() || !canSave()">
          Enregistrer
        </app-btn>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      max-height: 90vh;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      border-bottom: 1px solid var(--border-soft);

      h2 {
        margin: 0;
        font-family: var(--font-display);
        font-size: 22px;
        font-weight: 800;
        letter-spacing: -0.02em;
        color: var(--text-primary);
      }

      .close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        border-radius: 50%;
        cursor: pointer;
        color: var(--text-secondary);
        transition: all 0.15s ease;

        &:hover {
          background: var(--surface-sunken);
          color: var(--text-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--focus-ring-color);
          outline-offset: 2px;
        }
      }
    }

    .dialog-content {
      padding: 24px;
      overflow-y: auto;
    }

    .avatar-preview-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 24px;

      .avatar-preview {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
        box-shadow: var(--shadow-card);

        &.uploading {
          opacity: 0.7;
        }

        .upload-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
      }

      .preview-label {
        margin-top: 10px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--text-tertiary);
      }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .options-section {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .option-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border: 1px solid var(--border-soft);
      border-radius: 20px;
      cursor: pointer;
      transition: all 0.15s ease;
      background: var(--surface-card);

      &:hover {
        border-color: var(--border-strong);
        background: var(--surface-soft);
      }

      &.active {
        border-color: var(--border-unseen);
        background: var(--surface-unseen);

        .option-icon {
          background: var(--action-bg);
          color: var(--action-fg);
        }
      }

      .option-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-tile);
        background: var(--surface-sunken);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary);
        flex-shrink: 0;
        transition: all 0.15s ease;
      }

      .option-content {
        flex: 1;
        min-width: 0;

        h3 {
          margin: 0 0 2px;
          font-family: var(--font-display);
          font-size: 15px;
          font-weight: 700;
          color: var(--text-primary);
        }

        p {
          margin: 0;
          font-size: 12px;
          color: var(--text-secondary);
        }
      }

      .check-icon {
        color: var(--text-accent);
        flex-shrink: 0;
      }
    }

    .upload-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 24px;
      border: 2px dashed var(--border-dashed);
      border-radius: 20px;
      background: var(--surface-soft);
      cursor: pointer;
      transition: all 0.15s ease;
      margin-top: 4px;

      &:hover, &.dragover {
        border-color: var(--brand-orange);
        background: var(--surface-unseen);
      }

      ng-icon {
        color: var(--text-tertiary);
      }

      p {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: var(--text-body);
      }

      .hint {
        font-size: 12px;
        color: var(--text-tertiary);
      }
    }

    .url-input-area {
      margin-top: 8px;
      padding: 0 4px;
    }

    .dialog-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 24px;
      border-top: 1px solid var(--border-soft);
      background: var(--surface-soft);
    }
  `]
})
export class AvatarChangeDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AvatarChangeDialogComponent>);
  readonly data = inject<AvatarChangeDialogData>(MAT_DIALOG_DATA);
  private authService = inject(AuthService);
  private supabaseService = inject(SupabaseService);
  private notifications = inject(NotificationsService);

  // State
  public selectedMode = signal<AvatarMode>('upload');
  public previewUrl = signal<string | null>(null);
  public isUploading = signal(false);
  public isDragOver = signal(false);
  public customUrl = '';

  private uploadedFileUrl: string | null = null;

  constructor() {
    // Initialize with current avatar
    if (this.data?.currentPhotoUrl) {
      this.previewUrl.set(this.data.currentPhotoUrl);
      this.customUrl = this.data.currentPhotoUrl;
      this.selectedMode.set('current');
    }
  }

  /** Nom complet passé à app-avatar pour les initiales et la couleur de ton. */
  fullName(): string {
    return `${this.data?.firstName || ''} ${this.data?.lastName || ''}`.trim();
  }

  getInitials(): string {
    const first = this.data?.firstName?.charAt(0) || '';
    const last = this.data?.lastName?.charAt(0) || '';
    return (first + last).toUpperCase();
  }

  selectMode(mode: AvatarMode): void {
    this.selectedMode.set(mode);

    if (mode === 'generated') {
      // Aperçu dessiné localement par app-avatar (initiales), sans service tiers
      this.previewUrl.set(null);
    } else if (mode === 'current') {
      this.previewUrl.set(this.customUrl || null);
    } else if (mode === 'upload' && this.uploadedFileUrl) {
      this.previewUrl.set(this.uploadedFileUrl);
    }
  }


  onCustomUrlChange(url: string): void {
    this.customUrl = url;
    if (this.isValidUrl(url)) {
      this.previewUrl.set(url);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processFile(file);
    }
    input.value = '';
  }

  private async processFile(file: File): Promise<void> {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.notifications.showError('Veuillez sélectionner une image valide');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.notifications.showError('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    this.isUploading.set(true);

    try {
      // Create preview first
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to Supabase Storage
      const userId = this.authService.profile?.id;
      const ext = file.name.split('.').pop();
      const fileName = `avatar-${userId}-${Date.now()}.${ext}`;

      const { data, error } = await this.supabaseService.client.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = this.supabaseService.client.storage
        .from('avatars')
        .getPublicUrl(fileName);

      if (publicUrlData?.publicUrl) {
        this.uploadedFileUrl = publicUrlData.publicUrl;
        this.previewUrl.set(publicUrlData.publicUrl);
        this.notifications.showSuccess('Image téléchargée avec succès');
      }
    } catch (error) {
      this.notifications.showError('Erreur lors du téléchargement de l\'image');
      this.previewUrl.set(this.data?.currentPhotoUrl || null);
    } finally {
      this.isUploading.set(false);
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  canSave(): boolean {
    const mode = this.selectedMode();
    if (mode === 'upload') {
      return !!this.uploadedFileUrl;
    }
    if (mode === 'generated') {
      return true;
    }
    if (mode === 'current') {
      return !!this.customUrl && this.isValidUrl(this.customUrl);
    }
    return false;
  }

  save(): void {
    let photoUrl: string | null = null;

    switch (this.selectedMode()) {
      case 'upload':
        photoUrl = this.uploadedFileUrl;
        break;
      case 'generated':
        // Photo vidée : app-avatar dessine les initiales localement, aux tons
        // de la charte (plus d'URL ui-avatars.com stockée en base).
        photoUrl = '';
        break;
      case 'current':
        photoUrl = this.customUrl;
        break;
    }

    this.dialogRef.close({ photoUrl });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
